import { useMutation } from '@apollo/client/react';
import { Alert, Button, Checkbox, Col, Form, Input, message, Row } from 'antd';
import { filter, forEach, map, toLower } from 'lodash';
import React, { useMemo, useState } from 'react';
import { toast } from '../apollo';
import { DeleteButton } from '../assets/svg';
import { TYPES, TYPES_LABELS } from '../common/constants';
import { formValidatorRules } from '../common/utils';
import CommonBulkConfirmationModal from './CommonBulkConfirmationModal';
import CommonDrawer from './CommonDrawer';
import CommonSelect from './CommonSelect';

const { required, characterWithoutWhiteSpace } = formValidatorRules;

const renderOptionUi = (isDesktopViewport) => {
  return (
    <>
      <Form.List name="options">
        {(fields, { add, remove }) => {
          return (
            <Row gutter={[15, 15]} className="width-percent-100">
              {fields.map((field) => (
                <Col span={24} key={field.key}>
                  <Row gutter={[30]}>
                    <Col flex="auto">
                      <Form.Item
                        {...field}
                        label="Value"
                        name={[field.name, 'option']}
                        validateTrigger={['onChange', 'onClick']}
                        rules={[
                          required,
                          characterWithoutWhiteSpace,
                          {
                            max: 250,
                            message: 'Maximum 250 characters are allowed',
                          },
                          ({ getFieldValue }) => ({
                            validator(rule, value) {
                              const trimmedValue = value?.trim();
                              const items = map(
                                getFieldValue()?.options,
                                (item) => item?.option?.trim(),
                              );
                              if (
                                filter(
                                  items,
                                  (val) =>
                                    toLower(val) === toLower(trimmedValue),
                                )?.length > 1 &&
                                trimmedValue
                              ) {
                                // eslint-disable-next-line prefer-promise-reject-errors
                                return Promise.reject(
                                  'should be a unique option',
                                );
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <Input placeholder="Enter Value" />
                      </Form.Item>
                    </Col>
                    <Col className="d-flex align-center">
                      <Form.Item
                        {...field}
                        label={isDesktopViewport && ' '}
                        name={[field.name, 'qcFail']}
                        fieldKey={[field.fieldKey, 'qcFail']}
                        valuePropName="checked"
                        className="mr-10 mb-0"
                      >
                        <Checkbox>Mark for QC fail</Checkbox>
                      </Form.Item>
                      {fields?.length > 2 && (
                        <Form.Item
                          label={isDesktopViewport && ' '}
                          className="mb-0"
                        >
                          <DeleteButton
                            className="pointer"
                            onClick={() => {
                              if (fields?.length > 2) remove(field?.name);
                            }}
                          />
                        </Form.Item>
                      )}
                    </Col>
                  </Row>
                </Col>
              ))}
              <Row>
                <Col className="mb-15">
                  <Button
                    className="grey-button"
                    shape="round"
                    onClick={(e) => {
                      if (fields?.length >= 20) {
                        e?.preventDefault();
                        message.destroy();
                        message.error('You can only add up to 20 options.');
                        return;
                      }
                      add();
                    }}
                    block
                  >
                    Add Option
                  </Button>
                </Col>
              </Row>
            </Row>
          );
        }}
      </Form.List>
    </>
  );
};

const renderYesNoOptions = () => (
  <>
    <Row gutter={20}>
      <Col span={12}>
        <Form.Item label="Value">
          <Input allowClear placeholder="Yes" disabled />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name="yesOption" label=" " valuePropName="checked">
          <Checkbox>Mark for QC fail</Checkbox>
        </Form.Item>
      </Col>
    </Row>

    <Row gutter={20}>
      <Col span={12}>
        <Form.Item label="Value">
          <Input allowClear placeholder="No" disabled />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item name="noOption" label=" " valuePropName="checked">
          <Checkbox>Mark for QC fail</Checkbox>
        </Form.Item>
      </Col>
    </Row>
  </>
);

const BulkEditStageItemDrawer = ({
  showDrawer,
  setShowDrawer,
  selectedInputType,
  setSelectedInputType,
  isSelectAllAcrossPages,
  exceptIds,
  paginationProp,
  selectedRows,
  startJobTracking,
  getBulkJobStatus,
  bulkAction,
  stageId,
  mutation,
  mutationResponseField,
  stageIdFieldName,
  itemIdsFieldName,
  exceptIdsFieldName,
  containerId,
  isPublishedParent = false,
  newItemCount = 0,
}) => {
  const [form] = Form.useForm();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const closeDrawer = () => {
    setShowDrawer(false);
    setSelectedInputType(null);
    form.resetFields();
  };

  const handleCancel = () => {
    const hasFormChanges = form.isFieldsTouched();

    if (hasFormChanges) {
      setShowConfirmationModal(true);
    } else {
      closeDrawer();
    }
  };

  const handleConfirmDiscard = () => {
    closeDrawer();
  };

  const [bulkUpdateStageItems, { loading: bulkUpdateLoading }] = useMutation(
    mutation,
    {
      onError() {},
      onCompleted(response) {
        const { status, jobId } = response?.[mutationResponseField] || {};

        if (status === 'SUCCESS' && jobId) {
          startJobTracking(jobId, bulkAction);
          getBulkJobStatus({
            variables: { jobId },
          });
        }
        closeDrawer();
      },
    },
  );

  const isTypeDisabled = useMemo(() => {
    if (!isPublishedParent) return false;
    if (isSelectAllAcrossPages) {
      return newItemCount === 0;
    }
    return selectedRows?.every((row) => row?.projectEqcTypeStageItemId);
  }, [isPublishedParent, isSelectAllAcrossPages, newItemCount, selectedRows]);

  const yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];

  const inputTypeOptions = Object.keys(TYPES).map((key) => ({
    label: TYPES_LABELS[key],
    value: TYPES[key],
  }));

  const handleInputTypeSelect = (value) => {
    setSelectedInputType(value);
    // Reset type-specific fields when input type changes
    const currentValues = form.getFieldsValue();
    const fieldsToReset = [
      'yesOption',
      'noOption',
      'operator',
      'value',
      'qcFail',
      'options',
    ];
    const resetFields = {};
    fieldsToReset.forEach((field) => {
      resetFields[field] = undefined;
    });
    form.setFieldsValue({ ...currentValues, ...resetFields });

    if (value === TYPES.OPTION) {
      form.setFieldsValue({ options: [{}, { qcFail: true }] });
    } else if (value === TYPES['Y/N']) {
      form.setFieldsValue({ noOption: true });
    }
  };

  const renderConditionalFields = () => {
    switch (selectedInputType) {
      case TYPES['Y/N']:
        return renderYesNoOptions();
      case TYPES.NUMERIC:
        return null;
      case TYPES.OPTION:
        return renderOptionUi();
      case TYPES.TEXT:
        return null;
      default:
        return null;
    }
  };

  const onFormSubmit = async (formValues) => {
    if (Object.values(formValues).every((value) => value == null)) {
      toast({
        message: 'Please select at least one field to update',
        type: 'warning',
      });
      return;
    }
    let options = [];
    if (formValues?.type === TYPES?.['Y/N']) {
      options = [
        {
          option: 'Yes',
          qcFail: formValues.yesOption,
        },
        {
          option: 'No',
          qcFail: formValues.noOption,
        },
      ];
    }
    if (formValues.type === TYPES.OPTION) {
      forEach(formValues?.options, (option) => {
        const typeOptionValue = {
          option: option?.option?.trim(),
          qcFail: option?.qcFail || false,
        };
        options.push(typeOptionValue);
      });
      const checkCheckedValue = (value) => {
        return value.qcFail === false;
      };
      const isValid = options.some(checkCheckedValue);
      if (!isValid) {
        toast({
          message: 'At least one option should be marked as QcPass',
          type: 'error',
        });
        return;
      }
    }

    const newFormValues = {
      ...(!isTypeDisabled && { type: formValues?.type }),
      photo: formValues?.photo,
      remark: formValues?.remark,
      [stageIdFieldName]: Number(stageId),
      ...(!isTypeDisabled && options?.length > 0 && { options }),
    };

    if (isSelectAllAcrossPages) {
      newFormValues.isAllSelected = isSelectAllAcrossPages;
      if (exceptIds?.length > 0) {
        newFormValues[exceptIdsFieldName] = exceptIds;
      }
    } else {
      newFormValues[itemIdsFieldName] =
        selectedRows?.map((row) => row?.id) || [];
    }

    try {
      await bulkUpdateStageItems({
        variables: { data: { ...newFormValues } },
      });
    } catch (error) {
      return error;
    }
  };

  return (
    <>
      <CommonDrawer
        title={`Bulk Edit (${
          isSelectAllAcrossPages
            ? (paginationProp?.total || 0) - (exceptIds?.length || 0)
            : selectedRows?.length || 0
        } Items Selected)`}
        open={showDrawer}
        onClose={handleCancel}
        width={500}
        getContainer={() =>
          // eslint-disable-next-line no-undef
          document.getElementById(containerId) || document.body
        }
      >
        <Alert
          className="mb-10"
          title="Note: Only the current changes will be updated. All other configurations will remain unchanged."
          type="info"
          showIcon
        />
        <Form form={form} layout="vertical" onFinish={onFormSubmit}>
          <Form.Item name="type" label="Input Type">
            <CommonSelect
              placeholder="Select"
              options={inputTypeOptions}
              onChange={handleInputTypeSelect}
              allowClear
              disabled={isTypeDisabled}
            />
          </Form.Item>
          {isTypeDisabled && (
            <Alert
              className="mb-10"
              title="Type cannot be changed for checkpoints from the published version."
              type="warning"
              showIcon
            />
          )}
          {!isTypeDisabled && renderConditionalFields()}
          <Form.Item name="photo" label="Photos">
            <CommonSelect
              placeholder="Select"
              options={yesNoOptions}
              allowClear
            />
          </Form.Item>
          <Form.Item name="remark" label="Remarks">
            <CommonSelect
              placeholder="Select"
              options={yesNoOptions}
              allowClear
            />
          </Form.Item>
          <div className="form-buttons">
            <Button
              shape="round"
              className="cancel-button"
              disabled={bulkUpdateLoading}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              shape="round"
              type="primary"
              className="save-button"
              htmlType="submit"
              loading={bulkUpdateLoading}
            >
              Save
            </Button>
          </div>
        </Form>
      </CommonDrawer>

      {showConfirmationModal && (
        <CommonBulkConfirmationModal
          showModal={showConfirmationModal}
          setShowModal={setShowConfirmationModal}
          onConfirm={handleConfirmDiscard}
          title="Unsaved Changes"
          message="You have changes that haven't been saved. Discard them?"
        />
      )}
    </>
  );
};

export default BulkEditStageItemDrawer;
